import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router'
import { ImagePlus, Loader2, Settings, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { SidebarContent } from '@/components/ui/sidebar'
import { ModelPicker } from '@/components/generate/ModelPicker'
import { AspectRatioPicker } from '@/components/generate/AspectRatioPicker'
import { DurationPicker } from '@/components/generate/DurationPicker'
import { ResolutionPicker } from '@/components/generate/ResolutionPicker'
import { useGenerateWorkspace } from '@/context/GenerateWorkspaceContext'

const MAX_IMAGE_BYTES = 8 * 1024 * 1024
const IMAGE_ACCEPT = 'image/png,image/jpeg,image/webp'

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader()
    r.onload = () => {
      if (typeof r.result === 'string') resolve(r.result)
      else reject(new Error('Could not read file'))
    }
    r.onerror = () => reject(new Error('Could not read file'))
    r.readAsDataURL(file)
  })
}

function ImageUploadZone({
  label,
  value,
  onChange,
  disabled,
}: {
  label: string
  value: string | null
  onChange: (dataUrl: string | null) => void
  disabled?: boolean
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)

  async function handleFiles(files: FileList | null) {
    const file = files?.[0]
    if (!file || !file.type.startsWith('image/') || file.size > MAX_IMAGE_BYTES) return
    try {
      onChange(await readFileAsDataUrl(file))
    } catch {
      // Ignore unreadable files
    }
  }

  if (value) {
    return (
      <div className="flex flex-col gap-1">
        <p className="text-xs font-medium text-muted-foreground">{label}</p>
        <div className="relative overflow-hidden rounded-lg border border-border">
          <img src={value} alt={label} className="h-28 w-full object-cover" />
          <button
            type="button"
            onClick={() => onChange(null)}
            disabled={disabled}
            className="absolute right-1.5 top-1.5 rounded bg-black/60 p-0.5 text-white hover:bg-black/80 disabled:opacity-50"
          >
            <X className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            disabled={disabled}
            onClick={() => inputRef.current?.click()}
            className="absolute bottom-0 left-0 right-0 bg-black/50 py-0.5 text-center text-[10px] font-medium text-white hover:bg-black/70 disabled:opacity-50"
          >
            Replace
          </button>
          <input
            ref={inputRef}
            type="file"
            accept={IMAGE_ACCEPT}
            className="sr-only"
            onChange={(e) => void handleFiles(e.target.files)}
          />
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-1">
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
      <button
        type="button"
        disabled={disabled}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault()
          setDragging(true)
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault()
          setDragging(false)
          void handleFiles(e.dataTransfer.files)
        }}
        className={`flex h-24 w-full flex-col items-center justify-center gap-1.5 rounded-lg border-2 border-dashed transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
          dragging
            ? 'border-primary bg-primary/5'
            : 'border-border bg-input/20 hover:border-primary/50 hover:bg-input/40'
        }`}
      >
        <ImagePlus className="h-5 w-5 text-muted-foreground" />
        <span className="text-xs text-muted-foreground">Drop or click to upload</span>
        <span className="text-[10px] text-muted-foreground/60">PNG, JPG, WebP · max 8 MB</span>
      </button>
      <input
        ref={inputRef}
        type="file"
        accept={IMAGE_ACCEPT}
        className="sr-only"
        onChange={(e) => void handleFiles(e.target.files)}
      />
    </div>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────

export function ImageToVideoFormSidebar() {
  const {
    providerName,
    apiKey,
    keysLoading,
    models,
    modelId,
    setModelId,
    setPrompt,
    prompt,
    aspectRatio,
    setAspectRatio,
    resolution,
    setResolution,
    resolutionOptions,
    durationSeconds,
    setDurationSeconds,
    durationOptions,
    imageUrl,
    setImageUrl,
    lastFrameImageUrl,
    setLastFrameImageUrl,
    busy,
    handleGenerate,
  } = useGenerateWorkspace()

  const [mode, setMode] = useState<'reference' | 'first_last'>('reference')

  // lastFrame (Start+End) requires exactly 8 s. 1080p/4K also require 8 s.
  // WHY: Veo API rejects durationSeconds < 8 when config.lastFrame is set or resolution is high.
  useEffect(() => {
    if (
      mode === 'first_last' ||
      ((resolution === '1080p' || resolution === '4k') &&
        (durationSeconds === 4 || durationSeconds === 6))
    ) {
      setDurationSeconds(8)
    }
  }, [mode, resolution, durationSeconds, setDurationSeconds])

  const disabledDurations =
    mode === 'first_last' || resolution === '1080p' || resolution === '4k' ? [4, 6] : []

  // lastFrame is only supported on Veo 3.1 and Veo 3.1 Fast.
  const LAST_FRAME_MODEL_IDS = ['veo-3.1-generate-preview', 'veo-3.1-fast-generate-preview']

  useEffect(() => {
    if (mode === 'first_last' && !LAST_FRAME_MODEL_IDS.includes(modelId)) {
      const fallback = models.find((m) => LAST_FRAME_MODEL_IDS.includes(m.id))
      if (fallback) setModelId(fallback.id)
    }
  }, [mode, modelId, models, setModelId])

  const visibleModels =
    mode === 'first_last' ? models.filter((m) => LAST_FRAME_MODEL_IDS.includes(m.id)) : models

  // Clear image state that's not relevant to the current mode
  useEffect(() => {
    if (mode === 'reference') setLastFrameImageUrl(null)
    else setImageUrl(null)
  }, [mode, setImageUrl, setLastFrameImageUrl])

  const canGenerate =
    !!apiKey && !busy && (mode === 'reference' ? !!imageUrl : !!imageUrl && !!lastFrameImageUrl)

  return (
    <SidebarContent>
      <div className="flex flex-col gap-3 p-4">
        {apiKey === null && !keysLoading && (
          <div className="rounded-lg border border-amber-500/20 bg-amber-500/10 px-3 py-2.5 text-xs text-amber-400">
            No {providerName} key.{' '}
            <Link to="/apps/settings" className="underline underline-offset-2 hover:text-amber-300">
              Add in Settings
            </Link>
          </div>
        )}

        <ModelPicker models={visibleModels} value={modelId} onChange={setModelId} />

        {/* Mode toggle */}
        <div className="flex rounded-lg bg-muted p-0.5 gap-0.5">
          {(['reference', 'first_last'] as const).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              className={`flex-1 rounded-md py-1 text-xs font-semibold transition-colors ${
                mode === m
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {m === 'reference' ? 'Reference Image' : 'Start + End'}
            </button>
          ))}
        </div>

        {/* Image upload(s) */}
        {mode === 'reference' ? (
          <ImageUploadZone
            label="Reference Image"
            value={imageUrl}
            onChange={setImageUrl}
            disabled={busy}
          />
        ) : (
          <div className="grid grid-cols-2 gap-2">
            <ImageUploadZone
              label="First Frame"
              value={imageUrl}
              onChange={setImageUrl}
              disabled={busy}
            />
            <ImageUploadZone
              label="Last Frame"
              value={lastFrameImageUrl}
              onChange={setLastFrameImageUrl}
              disabled={busy}
            />
          </div>
        )}

        {/* Prompt */}
        <div className="flex flex-col gap-1">
          <p className="text-xs font-medium text-muted-foreground">Prompt (optional)</p>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="Describe the motion or scene…"
            rows={4}
            disabled={busy}
            className="w-full resize-y rounded-md border border-border bg-input/30 px-2.5 py-1.5 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
          />
        </div>

        <AspectRatioPicker
          value={aspectRatio}
          onChange={setAspectRatio}
          options={['16:9', '9:16']}
        />

        {durationOptions !== null && (
          <DurationPicker
            options={durationOptions}
            value={durationSeconds}
            onChange={setDurationSeconds}
            disabledOptions={disabledDurations}
          />
        )}

        {resolutionOptions !== null && (
          <ResolutionPicker
            options={resolutionOptions}
            value={resolution}
            onChange={setResolution}
          />
        )}

        <Button
          variant="cta"
          onClick={() => void handleGenerate()}
          disabled={!canGenerate}
          className="w-full"
        >
          {busy ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Generating…
            </>
          ) : (
            'Generate Video'
          )}
        </Button>

        {busy && (
          <p className="text-center text-xs text-muted-foreground">
            Video generation takes 1–6 minutes. Please wait.
          </p>
        )}

        <Link
          to="/apps/settings"
          className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
        >
          <Settings className="h-3.5 w-3.5" />
          Manage API Keys
        </Link>
      </div>
    </SidebarContent>
  )
}
